import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import type { AseguradoraDTO, CreateMetaRequest, MetaDTO, WrapperResponse } from '../../../model/kartera-api-model'
import APIInvoke from '../../../utils/APIInvoke'
import {
    ALL_INSURERS,
    ANNUAL,
    currentYear,
    errorMessage,
    formatAmount,
    formatCount,
    GOALS_BACK_PATH,
    money,
    monthNames,
    parseAmount,
    parseDate,
    sanitizeDecimal,
    WHOLE_PORTFOLIO,
} from '../common/goal-common'
import type { FormMessage } from '../common/goal-common'

/** `anual` o el índice del mes (`'0'` = enero). */
export type GoalPeriod = string

export interface GoalRow {
    key: string
    id: number | null
    aseguradora: string
    ramo: string
    anio: number
    periodo: GoalPeriod
    polizas: string
    primas: string
    message: FormMessage | null
    saving: boolean
}

export interface InsurerOption {
    id: string
    label: string
    short: string
}

const LOAD_ERROR = 'No se pudieron cargar las metas.'
const SAVE_ERROR = 'Error al guardar la meta'
const DELETE_ERROR = 'No se pudo eliminar la meta.'

const ALL_INSURERS_OPTION: InsurerOption = { id: ALL_INSURERS, label: 'Todas las aseguradoras', short: 'Todas las aseguradoras' }

export const RAMO_OPTIONS = [{ id: WHOLE_PORTFOLIO, label: 'Cartera total' }]

let localSeq = 0
const nextKey = () => `nueva-${++localSeq}`

const toRow = (meta: MetaDTO): GoalRow => {
    const periodo = parseDate(meta.periodo)
    return {
        key: `meta-${meta.id}`,
        id: meta.id,
        aseguradora: meta.aseguradora?.id != null ? String(meta.aseguradora.id) : ALL_INSURERS,
        ramo: !meta.modelo ? WHOLE_PORTFOLIO : String(meta.modelo),
        anio: periodo?.year ?? meta.anio ?? currentYear(),
        periodo: meta.tipo === ANNUAL ? ANNUAL : String((periodo?.month ?? meta.mes ?? 1) - 1),
        polizas: String(meta.polizas ?? 0),
        primas: formatAmount(meta.primas ?? 0),
        message: null,
        saving: false,
    }
}

const emptyRow = (): GoalRow => ({
    key: nextKey(),
    id: null,
    aseguradora: ALL_INSURERS,
    ramo: WHOLE_PORTFOLIO,
    anio: currentYear(),
    periodo: ANNUAL,
    polizas: '0',
    primas: formatAmount(0),
    message: null,
    saving: false,
})

const toRequest = (row: GoalRow): CreateMetaRequest => ({
    id: row.id,
    aseguradoraId: row.aseguradora === ALL_INSURERS ? null : Number(row.aseguradora),
    anio: row.anio,
    mes: row.periodo === ANNUAL ? 0 : Number(row.periodo) + 1,
    tipo: row.periodo === ANNUAL ? 'anual' : 'mensual',
    modelo: row.ramo === WHOLE_PORTFOLIO ? 0 : Number(row.ramo),
    polizas: Number(row.polizas) || 0,
    primas: parseAmount(row.primas),
})

const toInsurer = (item: AseguradoraDTO): InsurerOption => ({
    id: String(item.id),
    label: item.aseguradora || item.abreviacion || 'Aseguradora',
    short: item.abreviacion || item.aseguradora || 'Aseguradora',
})

export const useGoalListModel = () => {
    const location = useLocation()
    const backUrl: string = (location.state as { backUrl?: string } | null)?.backUrl || GOALS_BACK_PATH

    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [rows, setRows] = useState<GoalRow[]>([])
    const [insurers, setInsurers] = useState<InsurerOption[]>([ALL_INSURERS_OPTION])
    const [expanded, setExpanded] = useState<string | null>(null)
    const [deleteKey, setDeleteKey] = useState<string | null>(null)
    const [deleting, setDeleting] = useState(false)
    const rowsRef = useRef(rows)
    rowsRef.current = rows

    useEffect(() => {
        let active = true
        const load = async () => {
            setLoading(true)
            try {
                const insurersResponse: WrapperResponse<AseguradoraDTO[]> | null = await (await APIInvoke.invokeGET('/aseguradoras/registradas')).json()
                if (active && insurersResponse?.ok) setInsurers([ALL_INSURERS_OPTION, ...(insurersResponse.body || []).map(toInsurer)])
            } catch (insurersError) {
                console.error(insurersError)
            }
            try {
                const response: WrapperResponse<MetaDTO[]> | null = await (await APIInvoke.invokeGET('/metas')).json()
                if (!active) return
                if (!response?.ok) throw new Error(response?.message || LOAD_ERROR)
                const loaded = (response.body || []).map(toRow)
                setRows(loaded)
                setExpanded(loaded[0]?.key ?? null)
            } catch (loadError) {
                console.error(loadError)
                if (active) setError(errorMessage(loadError, LOAD_ERROR))
            } finally {
                if (active) setLoading(false)
            }
        }
        load()
        return () => { active = false }
    }, [])

    const months = useMemo(() => monthNames(), [])

    const periodOptions = useMemo(() => [
        { id: ANNUAL, label: 'Anual' },
        ...months.map((label, index) => ({ id: String(index), label })),
    ], [months])

    const yearOptions = useMemo(() => {
        const year = currentYear()
        const years = new Set([year - 1, year, year + 1, ...rows.map((row) => row.anio)])
        return [...years].sort((left, right) => left - right)
    }, [rows])

    const patchRow = (key: string, partial: Partial<GoalRow>) =>
        setRows((current) => current.map((row) => (row.key === key ? { ...row, ...partial } : row)))

    const change = {
        aseguradora: (key: string, value: string) => patchRow(key, { aseguradora: value }),
        ramo: (key: string, value: string) => patchRow(key, { ramo: value }),
        anio: (key: string, value: number) => patchRow(key, { anio: value }),
        periodo: (key: string, value: GoalPeriod) => patchRow(key, { periodo: value }),
        polizas: (key: string, value: string) => patchRow(key, { polizas: value.replace(/\D/g, '').slice(0, 5) }),
        primas: (key: string, value: string) => patchRow(key, { primas: sanitizeDecimal(value) }),
        primasBlur: (key: string, value: string) => patchRow(key, { primas: formatAmount(parseAmount(value)) }),
    }

    const addGoal = () => {
        const row = emptyRow()
        setRows((current) => [...current, row])
        setExpanded(row.key)
    }

    const save = async (event: React.FormEvent<HTMLFormElement>, key: string) => {
        event.preventDefault()
        const row = rowsRef.current.find((item) => item.key === key)
        if (!row || row.saving) return
        try {
            patchRow(key, { saving: true, message: null })
            const request = toRequest(row)
            const response: WrapperResponse<MetaDTO> = row.id
                ? await (await APIInvoke.invokePUT(`/metas/${row.id}`, request)).json()
                : await (await APIInvoke.invokePOST('/metas', request)).json()
            if (response.ok) {
                patchRow(key, {
                    id: response.body?.id ?? row.id,
                    message: { type: 'success', message: response.message || 'Meta guardada correctamente' },
                })
            } else {
                patchRow(key, { message: { type: 'error', message: response.message || SAVE_ERROR } })
            }
        } catch (saveError) {
            console.error(saveError)
            patchRow(key, { message: { type: 'error', message: SAVE_ERROR } })
        } finally {
            patchRow(key, { saving: false })
        }
    }

    const askDelete = (key: string) => {
        const row = rowsRef.current.find((item) => item.key === key)
        if (!row) return
        if (row.id) setDeleteKey(key)
        else setRows((current) => current.filter((item) => item.key !== key))
    }

    const confirmDelete = async () => {
        const row = rowsRef.current.find((item) => item.key === deleteKey)
        if (!row?.id) return
        try {
            setDeleting(true)
            const response: WrapperResponse<null> = await (await APIInvoke.invokeDELETE(`/metas/${row.id}`)).json()
            if (response.ok) setRows((current) => current.filter((item) => item.key !== row.key))
            else patchRow(row.key, { message: { type: 'error', message: response.message || DELETE_ERROR } })
        } catch (deleteError) {
            console.error(deleteError)
            patchRow(row.key, { message: { type: 'error', message: DELETE_ERROR } })
        } finally {
            setDeleting(false)
            setDeleteKey(null)
        }
    }

    const insurerShort = (id: string) => insurers.find((item) => item.id === id)?.short || ALL_INSURERS_OPTION.short
    const periodLabel = (row: GoalRow) => `${row.periodo === ANNUAL ? 'Anual' : months[Number(row.periodo)] || ''} ${row.anio}`

    return {
        loading,
        error,
        backTo: backUrl,
        goals: rows.map((row) => ({
            ...row,
            title: insurerShort(row.aseguradora),
            periodLabel: periodLabel(row),
            summary: `${formatCount(Number(row.polizas) || 0)} pólizas · ${money(parseAmount(row.primas))}`,
            isNew: !row.id,
            open: expanded === row.key,
        })),
        insurers,
        periodOptions,
        yearOptions,
        deleteOpen: deleteKey !== null,
        deleting,
        change,
        toggle: (key: string) => setExpanded((current) => (current === key ? null : key)),
        addGoal,
        save,
        askDelete,
        confirmDelete,
        cancelDelete: () => setDeleteKey(null),
    }
}
